import React, { FC, Fragment, useEffect, useState } from 'react';
import InvoicesPageActions from 'components/invoices/invoices-page-actions/invoices-page-actions.component';
import InvoicesList from 'components/invoices/invoices-list/invoices-list.component';
import { InvoiceFilter } from 'models/invoice';
import { DocumentNode } from '@apollo/client';
import { getInvoicesExcelExportTemplate } from 'apollo/templates/invoices';
import { useLazyQuery } from '@apollo/client';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import moment from 'moment';
import { downloadBase64File } from 'utils/file';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

type InvoicesProps = {
    queryTemplate: DocumentNode;
    queryName: string;
};

const Invoices: FC<InvoicesProps> = ({ queryTemplate, queryName }: InvoicesProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [invoiceFilter, setInvoiceFilter] = useState<InvoiceFilter>({} as InvoiceFilter);
    const { showBoundary } = useDenthubErrorBoundary();

    const [getExcelInvoices, { loading }] = useLazyQuery(getInvoicesExcelExportTemplate, {
        fetchPolicy: 'network-only',
        onCompleted: ({ invoicesExcelExport }) => {
            const fileName = `invoices_export_${moment().format('YYYY-MM-DD')}.xlsx`;
            downloadBase64File('application/octet-stream', invoicesExcelExport, fileName);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        const filters = JSON.parse(localStorage.getItem('invoiceListFilter') || '{}');
        setInvoiceFilter(filters);
    }, []);

    const invoiceFilterChangeHandler = (invoiceFilter: InvoiceFilter) => {
        setInvoiceFilter(invoiceFilter);
        localStorage.setItem('invoiceListFilter', JSON.stringify(invoiceFilter));
    };

    const getFormattedFilters = (invoiceFilter: InvoiceFilter) => {
        const filters = { ...invoiceFilter };
        delete filters.clinic;
        delete filters.lab;
        return filters;
    };

    const ExcelInvoiceFilterChangeHandler = async (invoiceFilter: InvoiceFilter) => {
        setInvoiceFilter(invoiceFilter);
        localStorage.setItem('invoiceListFilter', JSON.stringify(invoiceFilter));
        await getExcelInvoices({
            variables: {
                filters: getFormattedFilters(invoiceFilter),
            },
        });
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Helmet>
                <title>{translate('orders:INVOICES')} | Denthub</title>
            </Helmet>
            <InvoicesPageActions
                onInvoiceFilterChange={invoiceFilterChangeHandler}
                filters={invoiceFilter}
                onExcelInvoiceFilterChange={ExcelInvoiceFilterChangeHandler}
            />
            <InvoicesList queryName={queryName} queryTemplate={queryTemplate} invoiceFilter={invoiceFilter} />
        </Fragment>
    );
};

export default Invoices;
